import CustomTooltip from '@components/charts/common/CustomTooltip'
import {
  EventAnalyticsGraphEventOpening,
  FetchEventAnalyticsGraphParams,
} from '@models/membership/event/EventAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedEvent } from '@store/membership/EventReducer'
import { Card, Empty, Spin, theme } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const EventOpening = () => {
  const { token } = theme.useToken()
  const event = useAppSelector(selectSelectedEvent())

  const [chartData, setChartData] = useState<EventAnalyticsGraphEventOpening[]>(
    []
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchData = async () => {
    if (!event) return

    setIsLoading(true)
    setError(undefined)

    const fetchParams: FetchEventAnalyticsGraphParams = {
      entity: 'event',
      entityId: event.id,
      kind: 'event-opening',
      startDate: dayjs(event.start).subtract(180, 'days').format('YYYY-MM-DD'),
      endDate: dayjs(event.end).format('YYYY-MM-DD'),
    }

    try {
      const { data } =
        await fetchAnalyticsGraph<EventAnalyticsGraphEventOpening[]>(
          fetchParams
        )
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [event])

  interface CustomizedAxisTickProps {
    x: number
    y: number
    stroke?: string
    payload: {
      value: string
    }
  }
  const CustomizedAxisTick: React.FC<CustomizedAxisTickProps> = ({
    x,
    y,
    payload,
  }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={20} y={0} dy={16} textAnchor="end" fill="#bbb">
          {dayjs(payload.value).format('DD MMM')}
        </text>
      </g>
    )
  }

  return (
    <Card
      style={{ alignContent: 'center', textAlign: 'center', minHeight: 400 }}
    >
      {isLoading && <Spin />}
      {(chartData.length === 0 || error) && !isLoading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
      )}
      {chartData.length > 0 && !isLoading && !error && (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <YAxis
              dataKey="count"
              tickLine={false}
              stroke={'#bbb'}
              width={25}
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              stroke={'#bbb'}
              height={20}
              tick={(props) => <CustomizedAxisTick {...props} />}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  type="bar"
                  {...props}
                  customLabelFormatter={(payload) =>
                    dayjs(payload.label).format('D MMM YY')
                  }
                />
              )}
            />
            <Bar dataKey="count" fill={token.colorPrimary} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}

export default EventOpening
